// Labels shared by the KYC queue and the per-investor verification screen.

import { formatDateTimeDdmmyyyy, formatRawDateTime } from '../../../utils/format';
import { parseServerTime } from '../../../utils/serverTime';

export const FIELD_LABELS: Record<string, string> = {
    first_name: 'First name', last_name: 'Last name', email: 'Email', gender: 'Gender', dob: 'Date of birth', occupation: 'Occupation',
    aadhar_number: 'Aadhaar number', pan_number: 'PAN', aadhar_address: 'Aadhaar address', permanent_address: 'Permanent address', current_address: 'Current address',
    city: 'City', state: 'State', pincode: 'PIN code', doc_aadhar_front: 'Aadhaar front photo', doc_aadhar_back: 'Aadhaar back photo',
    doc_pan_card: 'PAN card photo', account_holder_name: 'Account holder', account_number: 'Account number', ifsc_code: 'IFSC',
    bank_name: 'Bank name', doc_bank_passbook: 'Passbook photo', doc_bank_cheque: 'Cancelled cheque',
};

export const KYC_GROUPS: { title: string; keys: string[] }[] = [
    { title: 'Personal', keys: ['first_name', 'last_name', 'email', 'gender', 'dob', 'occupation'] },
    { title: 'Address', keys: ['city', 'state', 'pincode', 'aadhar_address', 'permanent_address', 'current_address'] },
    { title: 'Identity', keys: ['aadhar_number', 'doc_aadhar_front', 'doc_aadhar_back', 'pan_number', 'doc_pan_card'] },
];
export const BANK_GROUPS: { title: string; keys: string[] }[] = [
    { title: 'Account', keys: ['account_holder_name', 'account_number', 'ifsc_code', 'bank_name'] },
    { title: 'Proof', keys: ['doc_bank_passbook', 'doc_bank_cheque'] },
];

export const NOMINEE_FIELD_LABELS: Record<string, string> = {
    mou_type: 'MOU type', value_type: 'Valuation basis', name: 'Full name', mobile: 'Mobile', relation: 'Relationship', aadhar: 'Aadhaar number',
    gender: 'Gender', pan: 'PAN', dob: 'Date of birth', address: 'Address', allocation_pct: 'Allocation share',
    allocated_amount: 'Calculated amount', doc_aadhar: 'Aadhaar photo', doc_pan: 'PAN photo', doc_guardian: 'Guardian certificate',
};

export const SECTION_STATUS: Record<string, { label: string; cls: string; short: string }> = {
    complete: { label: 'Filled', short: '✓', cls: 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200' },
    draft: { label: 'Draft', short: '…', cls: 'bg-indigo-50 text-indigo-800' },
    no_units: { label: 'No units', short: '–', cls: 'bg-slate-100 text-slate-500' },
    no_nominees: { label: 'No nominees yet', short: '+', cls: 'bg-orange-50 text-orange-800 ring-1 ring-orange-200' },
    missing: { label: 'Investor to add', short: '+', cls: 'bg-orange-50 text-orange-800 ring-1 ring-orange-200' },
    not_started: { label: 'Not started', short: '–', cls: 'bg-slate-100 text-slate-500' },
    in_review: { label: 'In review', short: '•', cls: 'bg-amber-100 text-amber-900' },
    needs_correction: { label: 'Needs correction', short: '!', cls: 'bg-red-100 text-red-800' },
    verified: { label: 'Verified', short: '✓', cls: 'bg-emerald-100 text-emerald-900' },
    locked: { label: 'Not yet', short: '–', cls: 'bg-slate-100 text-slate-500 ring-1 ring-slate-200' },
    ready: { label: 'Ready to send', short: '→', cls: 'bg-[#1B4332] text-white' },
    sent: { label: 'Sent to investor', short: '→', cls: 'bg-indigo-100 text-indigo-800' },
    done: { label: 'Acknowledged', short: '✓', cls: 'bg-[#1B4332] text-white' },
};

// Per-section acknowledgment (utils/kyc_flow.section_ack).
// A verified section (or unit) the investor has also acknowledged shows as "Acknowledged".
export const ackedStatus = (status: string, ack?: string | null) => (status === 'verified' && ack === 'done' ? 'done' : status);

export const ACK_STATUS: Record<string, { label: string; short: string; cls: string }> = {
    locked: { label: 'Not verified yet', short: '–', cls: 'bg-slate-100 text-slate-500 ring-1 ring-slate-200' },
    ready: { label: 'Ready to send', short: '→', cls: 'bg-[#1B4332] text-white' },
    sent: { label: 'Sent · awaiting investor', short: '→', cls: 'bg-indigo-100 text-indigo-800' },
    done: { label: 'Acknowledged', short: '✓', cls: 'bg-emerald-100 text-emerald-900' },
    'n/a': { label: 'Not needed', short: '·', cls: 'bg-slate-50 text-slate-400' },
};
export const ACK_SECTION_LABELS: Record<string, string> = { kyc: 'KYC details', bank: 'Bank details', nominees: 'Unit nominees' };

export const BUCKETS: { key: string; label: string; cls: string }[] = [
    { key: 'pending', label: 'Pending', cls: 'bg-orange-50 text-orange-800 ring-1 ring-orange-200' },
    { key: 'in_review', label: 'In review', cls: 'bg-amber-100 text-amber-900' },
    { key: 'awaiting_ack', label: 'Awaiting ack', cls: 'bg-indigo-100 text-indigo-800' },
    { key: 'completed', label: 'Completed', cls: 'bg-emerald-100 text-emerald-900' },
];

export const fmtWhen = (ts?: string | null) => {
    if (!ts) return '—';
    const d = parseServerTime(ts);
    if (isNaN(d.getTime())) return '—';
    return formatDateTimeDdmmyyyy(d);
};

// "1966-01-01" (or an ISO datetime) -> "01-01-1966"; anything unparseable is shown as is.
export const fmtDate = (v: any) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
    return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
};

export const waiting = (ts?: string | null) => {
    if (!ts) return { text: '—', late: false };
    const h = (Date.now() - parseServerTime(ts).getTime()) / 36e5;
    if (isNaN(h)) return { text: '—', late: false };
    return { text: h < 1 ? '<1 h' : h < 48 ? `${Math.floor(h)} h` : `${Math.floor(h / 24)} d`, late: h >= 48 };
};

export const inr = (v: any) => {
    const n = Number(v);
    return v == null || v === '' || isNaN(n) ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`;
};

// "Vineela lagadapati (6281507658)" — the actor on a history / audit entry.
export const actorName = (h: any) => {
    if (!h) return '';
    const name = h.by_name || (h.by === 'customer' ? 'Customer' : h.by || 'System');
    const mob = /^\d{10}$/.test(String(h.by || '')) && h.by !== name ? h.by : '';
    return mob ? `${name} (${mob})` : name;
};

// kyc | bank | nominees for a history entry (mirrors utils/kyc_flow.section_of).

// Date only, in the common DD-MM-YYYY form. user_created_date is stored in UTC, so go through
// formatRawDateTime (which shifts to IST) and drop its time, rather than slicing the raw date.
export const createdDate = (ts?: string | null) => (formatRawDateTime(ts) || '-').split(' • ')[0];
